import React, { FC } from 'react';
import { AnimalType, ESFReader, Equipment, ValveCycleDaysCalculationMethod } from 'shared/api/api';
import { localized } from 'shared/state/i18n/i18n';
import { SkioldCheckbox } from 'web/view/components/skiold-components/skiold-checkbox/skiold-checkbox';
import { SkioldFormsWrapper } from 'web/view/components/skiold-components/skiold-forms-wrapper/skiold-forms-wrapper';
import { FormRow } from 'web/view/components/skiold-components/skiold-forms-wrapper/skiold-forms-wrapper-types';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { TextWeb } from 'web/web-helpers/styled-text-components';
import { useGeneralSetting } from './general-settings-overview';
import { useSelector } from 'react-redux';
import { SharedAppState } from 'shared/state/store.shared';
import { SkioldFormDropdown } from 'web/view/components/skiold-components/skiold-dropdown/skiold-form-dropdown';
import {
	ESFReaderOptions,
	IsElistarOrEsf,
} from 'shared/helpers/work-list-helpers/esf-list-helpers/generel-esf-list-helper';
import { Option } from 'react-dropdown';
import { SkioldFormIntegerInput } from 'web/view/components/skiold-components/skiold-integer-input/skiold-form-integer-input';
import { FlexRow } from 'web/view/components/basic-flex/flex-row';
import { getNotifyCapacitySows } from './warning-settings';
import { SkioldInput } from 'web/view/components/skiold-components/skiold-input/skiold-input';

interface Props {
	animalType: AnimalType;
}

export const GeneralSettingsCommunication: FC<Props> = React.memo(({ animalType }) => {
	const { generalSetting, setGeneralSetting } = useGeneralSetting();
	const hasDistriwin = useSelector((state: SharedAppState) =>
		state.processEquipments.entities.find(e => e.equipment === Equipment.Distriwin),
	);

	const hasElistar = useSelector((state: SharedAppState) =>
		state.processEquipments.entities.find(e => e.equipment === Equipment.Elistar),
	);
	const hasEsf = useSelector((state: SharedAppState) =>
		state.processEquipments.entities.find(e => IsElistarOrEsf(e.equipment)),
	);
	const getFormRows = () => {
		let formRows = new Array<FormRow>();
		formRows.push(getWorkListHeader());
		formRows.push(getShareDataWithSegesInsight());

		// I know this looks odd, but apparently the order if crucial here,
		//  and grouped if statements are therefore not possible
		if (hasDistriwin && animalType === AnimalType.Sow) {
			formRows.push(getNotifyCapacitySows({ generalSetting, setGeneralSetting }));
		}

		if (hasEsf && animalType === AnimalType.Sow) {
			formRows.push(getReaderRow());
		}

		// if (hasDistriwin && animalType === AnimalType.Sow) {
		// 	formRows.push(getSendCycleDaysAsNegativeForPregnantSows());
		// }

		if ((hasEsf || hasDistriwin) && animalType === AnimalType.Sow) {
			formRows.push(getSendCycleDaysAsNegative());
		}

		if (hasDistriwin) {
			formRows.push(getFetchDistriwinFeeding());
		}

		if (hasDistriwin && animalType === AnimalType.Sow) {
			formRows.push(getSendEntranceDepartureDistriwinSows());
			formRows.push(getSendSowCycleDistriwinSows());
			formRows.push(getEnableDistriwinPenUpdate());
		}

		if (animalType !== AnimalType.Sow) {
			formRows.push(getSendDataToDistriwin());
			formRows.push(getSendEntranceDepartureToDistriwin());
			formRows.push(getNotifyCapacity());
		}

		if (hasEsf || hasElistar) {
			formRows.push(getElistarAdjustFeedRow());
			formRows.push(getResendCycleDaysElistarRow());
		}

		if (hasDistriwin && generalSetting.sendEntranceDepartureDistriwinSows) {
			formRows.push(getValveCycleDaysRow());
		}
		return formRows;
	};
	const getWorkListHeader = (): FormRow => {
		return {
			header: localized('Communication'),
		};
	};

	const getFetchDistriwinFeeding = (): FormRow => {
		return {
			name: localized('FetchDistriwinFeedingData'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={SetFetchFeedingDataTrue}
						isChecked={generalSetting.fetchDistriwinFeeding}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={SetFetchFeedingDataFalse}
						isChecked={!generalSetting.fetchDistriwinFeeding}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getElistarAdjustFeedRow = (): FormRow => {
		return {
			name: localized('ElistarAdjustFeedSetting'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<ViewWeb>
						<TextWeb>{localized('fixedPercent')}</TextWeb>
						<TextWeb>{localized('DeviationSlashDays')}</TextWeb>
					</ViewWeb>
					<ViewWeb>
						<SkioldCheckbox
							onClick={SetElistarAdjustFeedFixedDeviation}
							isChecked={generalSetting.elistarEsfAdjustFeedFixedDeviation}
						/>

						<SkioldCheckbox
							onClick={SetElistarAdjustTempFeedAndPeriodDevation}
							isChecked={generalSetting.elistarEsfAdjustTempFeedAndPeriodDevation}
						/>
					</ViewWeb>
				</ViewWeb>
			),
		};
	};

	const getSendCycleDaysAsNegative = (): FormRow => {
		return {
			name: localized('SendCycleDaysAsNegative'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={SetSendCycleDaysAsNegativeTrue}
						isChecked={generalSetting.sendCycleDaysAsNegative}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={SendCycleDaysAsNegativeFalse}
						isChecked={!generalSetting.sendCycleDaysAsNegative}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
			style: { height: 80 },
		};
	};

	const getSendCycleDaysAsNegativeForPregnantSows = (): FormRow => {
		return {
			name: localized('SendCycleDaysAsNegativeForPregnantSows'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={SetSendCycleDaysAsNegativeForPregnantSowsTrue}
						isChecked={generalSetting.sendCycleDaysAsNegative}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={SetSendCycleDaysAsNegativeForPregnantSowsFalse}
						isChecked={!generalSetting.sendCycleDaysAsNegative}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getShareDataWithSegesInsight = (): FormRow => {
		return {
			name: localized('ShareDataWithSegesInsight'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox onClick={ShareSegesInSightTrue} isChecked={generalSetting.segesInsightConsent} />
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox onClick={ShareSegesInSightFalse} isChecked={!generalSetting.segesInsightConsent} />
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getReaderRow = (): FormRow => {
		return {
			name: localized('Reader'),
			component: (
				<SkioldFormDropdown
					items={ESFReaderOptions}
					selectedValue={{
						value: generalSetting.reader ?? '',
						label: localized(generalSetting.reader ?? ''),
					}}
					onValueChanged={SetReader}
				/>
			),
		};
	};

	const getSendDataToDistriwin = (): FormRow => {
		const isChecked =
			animalType === AnimalType.Finisher
				? generalSetting.sendDataToDistriwinFinisher
				: generalSetting.sendDataToDistriwinWeaner;
		return {
			name: localized('SendDataToDistriwin'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox onClick={SetSendDataTrue} isChecked={isChecked} />
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox onClick={SetSendDataFalse} isChecked={!isChecked} />
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getSendEntranceDepartureToDistriwin = (): FormRow => {
		const isChecked =
			animalType === AnimalType.Finisher
				? generalSetting.sendEntranceDepartureDistriwinFinisher
				: generalSetting.sendEntranceDepartureDistriwinWeaner;
		return {
			name: localized('SendEntranceDepartureToDistriwin'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox onClick={SetSendEntranceDepartureTrue} isChecked={isChecked} />
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox onClick={SetSendEntranceDepartureFalse} isChecked={!isChecked} />
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const setValveCycleCalculationMethod = (method: ValveCycleDaysCalculationMethod) => {
		setGeneralSetting({ ...generalSetting, distriwinValveCycleDaysCalculationMethod: method });
	};

	const getValveCycleDaysRow = (): FormRow => {
		return {
			name: localized('ValveCycleDaysCalculationToMultiplePaths'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={() => setValveCycleCalculationMethod(ValveCycleDaysCalculationMethod.Min)}
						isChecked={
							generalSetting.distriwinValveCycleDaysCalculationMethod ===
							ValveCycleDaysCalculationMethod.Min
						}
					/>
					<TextWeb>{localized('Min')}</TextWeb>
					<SkioldCheckbox
						onClick={() => setValveCycleCalculationMethod(ValveCycleDaysCalculationMethod.Avg)}
						isChecked={
							generalSetting.distriwinValveCycleDaysCalculationMethod ===
							ValveCycleDaysCalculationMethod.Avg
						}
					/>
					<TextWeb>{localized('avg')}</TextWeb>
					<SkioldCheckbox
						onClick={() => setValveCycleCalculationMethod(ValveCycleDaysCalculationMethod.Max)}
						isChecked={
							generalSetting.distriwinValveCycleDaysCalculationMethod ===
							ValveCycleDaysCalculationMethod.Max
						}
					/>
					<TextWeb>{localized('Max')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getNotifyCapacity = (): FormRow => {
		const currentValue =
			animalType === AnimalType.Finisher
				? generalSetting.notifyCapacityFinisher
				: generalSetting.notifyCapacityWeaner
				? generalSetting.notifyCapacityWeaner
				: 0;

		const onChangeCapacity = (value: number | undefined) => {
			if (animalType === AnimalType.Finisher) {
				setGeneralSetting({ ...generalSetting, notifyCapacityFinisher: value });
			} else if (animalType === AnimalType.Weaner) {
				setGeneralSetting({ ...generalSetting, notifyCapacityWeaner: value });
			}
		};

		return {
			name: localized('NotifyCapacity'),
			component: <SkioldFormIntegerInput text={currentValue} onChangeNumber={onChangeCapacity} />,
		};
	};

	const getSendEntranceDepartureDistriwinSows = (): FormRow => {
		return {
			name: localized('SendEntranceDepartureDistriwinSows'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={SetSendEntranceDepartureDistriwinSowsTrue}
						isChecked={generalSetting.sendEntranceDepartureDistriwinSows}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={SetSendEntranceDepartureDistriwinSowsFalse}
						isChecked={!generalSetting.sendEntranceDepartureDistriwinSows}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getSendSowCycleDistriwinSows = (): FormRow => {
		return {
			name: localized('SendSowCycleDistriwinSows'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={SetSendSowCycleDistriwinSowsTrue}
						isChecked={generalSetting.sendSowCycleDistriwinSows}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={SetSendSowCycleDistriwinSowsFalse}
						isChecked={!generalSetting.sendSowCycleDistriwinSows}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};
	const getEnableDistriwinPenUpdate = (): FormRow => {
		return {
			name: localized('EnableDistriwinPenUpdate'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={SetEnableDistriwinPenUpdateTrue}
						isChecked={generalSetting.enableDistriwinPenUpdate}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={SetEnableDistriwinPenUpdateFalse}
						isChecked={!generalSetting.enableDistriwinPenUpdate}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const getResendCycleDaysElistarRow = (): FormRow => {
		return {
			name: localized('ShowUpdateCycleDaysEsfList'),
			component: (
				<ViewWeb className="two-checkbox-container">
					<SkioldCheckbox
						onClick={SetResendCycleDaysElistarTrue}
						isChecked={generalSetting.resendCycleDaysElistar}
					/>
					<TextWeb>{localized('True')}</TextWeb>
					<SkioldCheckbox
						onClick={SetResendCycleDaysElistarFalse}
						isChecked={!generalSetting.resendCycleDaysElistar}
					/>
					<TextWeb>{localized('False')}</TextWeb>
				</ViewWeb>
			),
		};
	};

	const SetFetchFeedingDataTrue = () => {
		setGeneralSetting({ ...generalSetting, fetchDistriwinFeeding: true });
	};

	const SetFetchFeedingDataFalse = () => {
		setGeneralSetting({ ...generalSetting, fetchDistriwinFeeding: false });
	};

	const SetSendCycleDaysAsNegativeTrue = () => {
		setGeneralSetting({ ...generalSetting, sendCycleDaysAsNegative: true });
	};

	const SendCycleDaysAsNegativeFalse = () => {
		setGeneralSetting({ ...generalSetting, sendCycleDaysAsNegative: false });
	};

	const SetSendCycleDaysAsNegativeForPregnantSowsTrue = () => {
		setGeneralSetting({ ...generalSetting, sendCycleDaysAsNegativeForPregnantSows: true });
	};

	const SetSendCycleDaysAsNegativeForPregnantSowsFalse = () => {
		setGeneralSetting({ ...generalSetting, sendCycleDaysAsNegativeForPregnantSows: false });
	};

	const ShareSegesInSightTrue = () => {
		setGeneralSetting({ ...generalSetting, segesInsightConsent: true });
	};

	const ShareSegesInSightFalse = () => {
		setGeneralSetting({ ...generalSetting, segesInsightConsent: false });
	};

	const SetReader = (option: Option) => {
		setGeneralSetting({
			...generalSetting,
			reader: option.value !== ' ' ? (option.value as ESFReader) : undefined,
		});
	};

	const SetSendDataTrue = () => {
		if (animalType === AnimalType.Finisher) {
			setGeneralSetting({ ...generalSetting, sendDataToDistriwinFinisher: true });
		} else if (animalType === AnimalType.Weaner) {
			setGeneralSetting({ ...generalSetting, sendDataToDistriwinWeaner: true });
		}
	};

	const SetSendDataFalse = () => {
		if (animalType === AnimalType.Finisher) {
			setGeneralSetting({ ...generalSetting, sendDataToDistriwinFinisher: false });
		} else if (animalType === AnimalType.Weaner) {
			setGeneralSetting({ ...generalSetting, sendDataToDistriwinWeaner: false });
		}
	};

	const SetSendEntranceDepartureTrue = () => {
		if (animalType === AnimalType.Finisher) {
			setGeneralSetting({ ...generalSetting, sendEntranceDepartureDistriwinFinisher: true });
		} else if (animalType === AnimalType.Weaner) {
			setGeneralSetting({ ...generalSetting, sendEntranceDepartureDistriwinWeaner: true });
		}
	};

	const SetSendEntranceDepartureFalse = () => {
		if (animalType === AnimalType.Finisher) {
			setGeneralSetting({ ...generalSetting, sendEntranceDepartureDistriwinFinisher: false });
		} else if (animalType === AnimalType.Weaner) {
			setGeneralSetting({ ...generalSetting, sendEntranceDepartureDistriwinWeaner: false });
		}
	};

	const SetSendEntranceDepartureDistriwinSowsTrue = () => {
		setGeneralSetting({ ...generalSetting, sendEntranceDepartureDistriwinSows: true });
	};

	const SetSendEntranceDepartureDistriwinSowsFalse = () => {
		setGeneralSetting({ ...generalSetting, sendEntranceDepartureDistriwinSows: false });
	};

	const SetSendSowCycleDistriwinSowsTrue = () => {
		setGeneralSetting({ ...generalSetting, sendSowCycleDistriwinSows: true });
	};

	const SetSendSowCycleDistriwinSowsFalse = () => {
		setGeneralSetting({ ...generalSetting, sendSowCycleDistriwinSows: false });
	};

	const SetEnableDistriwinPenUpdateTrue = () => {
		setGeneralSetting({ ...generalSetting, enableDistriwinPenUpdate: true });
	};

	const SetEnableDistriwinPenUpdateFalse = () => {
		setGeneralSetting({ ...generalSetting, enableDistriwinPenUpdate: false });
	};

	const SetElistarAdjustTempFeedAndPeriodDevation = () => {
		setGeneralSetting({
			...generalSetting,
			elistarEsfAdjustFeedFixedDeviation: false,
			elistarEsfAdjustTempFeedAndPeriodDevation: true,
		});
	};

	const SetElistarAdjustFeedFixedDeviation = () => {
		setGeneralSetting({
			...generalSetting,
			elistarEsfAdjustFeedFixedDeviation: true,
			elistarEsfAdjustTempFeedAndPeriodDevation: false,
		});
	};

	const SetResendCycleDaysElistarTrue = () => {
		setGeneralSetting({ ...generalSetting, resendCycleDaysElistar: true });
	};

	const SetResendCycleDaysElistarFalse = () => {
		setGeneralSetting({ ...generalSetting, resendCycleDaysElistar: false });
	};

	return <SkioldFormsWrapper formRows={getFormRows()} containerClassName="wrapper-container" />;
});
